import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api'; // A sua instância do axios
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const AgendaModal = ({ evento, onClose, onSave }) => {
    const [form, setForm] = useState({
        titulo: '',
        descricao: '',
        data: '', // campo apenas para data
        hora: '', // campo apenas para hora
        local: '',
    });
    const [formError, setFormError] = useState('');

    useEffect(() => {
        if (evento?.data_evento && evento?.hora) {
            // Usa data_evento e hora do backend
            const [hora, min] = evento.hora.split(':');
            setForm({
                titulo: evento.titulo || '',
                descricao: evento.descricao || '',
                data: evento.data_evento,
                hora: `${hora}:${min}`,
                local: evento.local || '',
            });
        } else if (evento?.data_evento) {
            // Fallback para UTC se não vier hora separada
            const d = new Date(evento.data_evento);
            const ano = d.getUTCFullYear();
            const mes = String(d.getUTCMonth() + 1).padStart(2, '0');
            const dia = String(d.getUTCDate()).padStart(2, '0');
            const horaUTC = String(d.getUTCHours()).padStart(2, '0');
            const minUTC = String(d.getUTCMinutes()).padStart(2, '0');
            setForm({
                titulo: evento.titulo || '',
                descricao: evento.descricao || '',
                data: `${ano}-${mes}-${dia}`,
                hora: `${horaUTC}:${minUTC}`,
                local: evento.local || '',
            });
        } else {
            const now = new Date();
            const ano = now.getUTCFullYear();
            const mes = String(now.getUTCMonth() + 1).padStart(2, '0');
            const dia = String(now.getUTCDate()).padStart(2, '0');
            const horaLocal = String(now.getHours()).padStart(2, '0');
            const minLocal = String(now.getMinutes()).padStart(2, '0');
            setForm({
                titulo: evento?.titulo || '',
                descricao: evento?.descricao || '',
                data: `${ano}-${mes}-${dia}`,
                hora: `${horaLocal}:${minLocal}`,
                local: evento?.local || '',
            });
        }
    }, [evento]);

    const handleChange = (e) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setFormError('');
        if (!form.titulo.trim()) {
            setFormError('O título é obrigatório.');
            return;
        }
        if (!form.data) {
            setFormError('A data é obrigatória.');
            return;
        }
        if (!form.hora) {
            setFormError('O horário é obrigatório.');
            return;
        }
        // Junta data e hora para criar ISO UTC
        let utcISOString = '';
        if (form.data && form.hora) {
            // Monta string ISO e converte para UTC
            const isoString = `${form.data}T${form.hora}:00.000Z`;
            const d = new Date(isoString);
            if (isNaN(d.getTime())) {
                setFormError('Data ou horário inválido.');
                return;
            }
            utcISOString = d.toISOString();
        }
        onSave({ ...evento, ...form, data_evento: utcISOString });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4" aria-modal="true" role="dialog">
            <div className="bg-white p-8 rounded-lg shadow-xl w-full max-w-lg animate-fade-in-scale">
                <h2 className="text-2xl font-bold mb-6">{evento?.id ? 'Editar Evento' : 'Adicionar Novo Evento'}</h2>
                <form onSubmit={handleSubmit} className="space-y-4" aria-label="Formulário de evento">
                    <input name="titulo" value={form.titulo} onChange={handleChange} placeholder="Título do Evento" className="w-full p-3 border rounded-lg" required aria-label="Título do Evento" />
                    <textarea name="descricao" value={form.descricao} onChange={handleChange} placeholder="Descrição" className="w-full p-3 border rounded-lg" rows="3" aria-label="Descrição" />
                    <input name="local" value={form.local} onChange={handleChange} placeholder="Local do Evento" className="w-full p-3 border rounded-lg" aria-label="Local do Evento" />
                    <div className="flex gap-4">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700" htmlFor="data-evento">Data do Evento</label>
                            <input name="data" id="data-evento" value={form.data} onChange={handleChange} type="date" className="w-full p-3 border rounded-lg mt-1" required aria-label="Data do Evento" />
                        </div>
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700" htmlFor="hora-evento">Horário do Evento</label>
                            <input name="hora" id="hora-evento" value={form.hora} onChange={handleChange} type="time" className="w-full p-3 border rounded-lg mt-1" required aria-label="Horário do Evento" />
                        </div>
                    </div>
                    {formError && <div className="text-red-500 mb-2" aria-live="assertive">{formError}</div>}
                    <div className="flex justify-end gap-4 pt-4">
                        <button type="button" onClick={onClose} className="px-5 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300" aria-label="Cancelar">Cancelar</button>
                        <button type="submit" className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" aria-label="Salvar">Salvar</button>
                    </div>
                </form>
            </div>
        </div>
    );
};


// --- Componente Principal da Página de Administração ---
export default function GerenciadorAgenda({ grupo, usuarioPainel, token }) {
    const [agendas, setAgendas] = useState([]);
    const [grupos, setGrupos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingEvento, setEditingEvento] = useState(null);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Busca dados usando usuarioPainel e token recebidos do painel
    const fetchData = useCallback(async (isBackground = false) => {
        if (!usuarioPainel || !token) return;
        if (!isBackground) setLoading(true);
        setError('');
        try {
            const [agendasRes, gruposRes] = await Promise.all([
                api.get('/agenda', {
                    params: {
                        paroquiaId: grupo.paroquia_id,
                        comunidadeId: grupo.comunidade_id,
                        grupoId: grupo.grupo_id,
                        showAll: true
                    },
                    headers: { Authorization: `Bearer ${token}` }
                }),
                api.get('/grupos', {
                    params: { paroquiaId: grupo.paroquia_id },
                    headers: { Authorization: `Bearer ${token}` }
                }),
            ]);
            setAgendas(agendasRes.data.sort((a, b) => new Date(b.data_evento) - new Date(a.data_evento)));
            setGrupos(gruposRes.data);
        } catch (error) {
            console.error('Erro ao carregar dados:', error);
            setError('Não foi possível carregar os dados da agenda.');
        } finally {
            if (!isBackground) setLoading(false);
        }
    }, [grupo, usuarioPainel, token]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleSave = async (eventoData) => {
        if (!usuarioPainel || !token) {
            toast.error("Erro: não foi possível identificar o utilizador. Tente novamente.");
            return;
        }
        setIsSubmitting(true);
        const payload = {
            ...eventoData,
            paroquia_id: grupo.paroquia_id,
            comunidade_id: grupo.comunidade_id,
            grupo_id: eventoData.id ? eventoData.grupo_id : grupo.grupo_id
        };
        try {
            let savedEvento;
            if (payload.id) {
                const res = await api.put(`/admin/agenda/${payload.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
                savedEvento = res.data;
                setAgendas(prev => prev.map(evt => evt.id === payload.id ? { ...evt, ...savedEvento } : evt).sort((a, b) => new Date(b.data_evento) - new Date(a.data_evento)));
                toast.success('Evento atualizado com sucesso!');
            } else {
                const res = await api.post('/admin/agenda', payload, { headers: { Authorization: `Bearer ${token}` } });
                savedEvento = res.data;
                setAgendas(prev => [savedEvento, ...prev].sort((a, b) => new Date(b.data_evento) - new Date(a.data_evento)));
                toast.success('Evento criado com sucesso!');
            }
            setIsModalOpen(false);
            setEditingEvento(null);
            fetchData(true);
        } catch (error) {
            toast.error('Falha ao salvar. Verifique os dados e tente novamente.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (id) => {
        // Modal de confirmação customizado
        const confirmDelete = window.confirm('Tem certeza que deseja excluir este evento?');
        if (!confirmDelete) return;
        setIsSubmitting(true);
        try {
            await api.delete(`/admin/agenda/${id}`, { headers: { Authorization: `Bearer ${token}` } });
            setAgendas(prev => prev.filter(evt => evt.id !== id));
            toast.success('Evento excluído com sucesso!');
            fetchData(true);
        } catch (error) {
            toast.error('Falha ao excluir.');
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const formatarData = (data, hora) => {
        if (!data) return '';
        // Se hora vier separada, junta data e hora para criar o Date
        let dateTimeStr = data;
        if (hora) {
            // Garante formato ISO completo para Date interpretado como UTC
            dateTimeStr = `${data}T${hora}Z`;
        }
        const d = new Date(dateTimeStr);
        if (isNaN(d.getTime())) return '';
        const diaUTC = String(d.getUTCDate()).padStart(2, '0');
        const mesUTC = String(d.getUTCMonth() + 1).padStart(2, '0');
        const anoUTC = d.getUTCFullYear();
        const horaUTC = String(d.getUTCHours()).padStart(2, '0');
        const minUTC = String(d.getUTCMinutes()).padStart(2, '0');
        return `${diaUTC}/${mesUTC}/${anoUTC} ${horaUTC}:${minUTC}`;
    };

    return (
        <div>
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} newestOnTop closeOnClick rtl={false} pauseOnFocusLoss draggable pauseOnHover />
            <style>{`.animate-fade-in-scale { animation: fadeInScale 0.3s ease-out forwards; } @keyframes fadeInScale { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }`}</style>
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-700">Gestão de Agenda</h2>
                <button onClick={() => { setEditingEvento(null); setIsModalOpen(true); }} className="mt-4 sm:mt-0 px-5 py-2 bg-indigo-600 text-white rounded-lg shadow-md hover:bg-indigo-700 transition-colors flex items-center gap-2" aria-label="Adicionar Evento" disabled={isSubmitting}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" /></svg>
                    Adicionar Evento
                </button>
            </div>

            {error && <p className="text-red-500 bg-red-100 p-3 rounded-md mb-4" aria-live="assertive">{error}</p>}

            {loading ? <p className="text-center text-gray-500">A carregar eventos...</p> : (
                <div className="bg-white rounded-xl overflow-hidden shadow-lg">
                    <ul className="divide-y divide-gray-200">
                        {agendas.length > 0 ? agendas.map(evento => (
                            <li key={evento.id} className="p-4 hover:bg-gray-50 transition-colors flex flex-col sm:flex-row justify-between sm:items-center">
                                <div className="mb-4 sm:mb-0">
                                    <p className="font-bold text-lg text-gray-900">{evento.titulo}</p>
                                    <p className="text-sm text-gray-600">{formatarData(evento.data_evento, evento.hora)} - {evento.local}</p>
                                    <p className="text-xs text-indigo-700 font-semibold mt-1">
                                        {grupos.find(g => g.id === evento.grupo_id)?.name || 'Grupo Desconhecido'}
                                    </p>
                                </div>
                                <div className="flex gap-2 self-end sm:self-center">
                                    <button onClick={() => { setEditingEvento(evento); setIsModalOpen(true); }} className="px-4 py-2 text-sm bg-yellow-500 text-white rounded-md hover:bg-yellow-600" aria-label="Editar Evento" disabled={isSubmitting}>Editar</button>
                                    <button onClick={() => handleDelete(evento.id)} className="px-4 py-2 text-sm bg-red-600 text-white rounded-md hover:bg-red-700" aria-label="Excluir Evento" disabled={isSubmitting}>Excluir</button>
                                </div>
                            </li>
                        )) : <li className="p-6 text-center text-gray-500">Nenhum evento cadastrado para o seu grupo.</li>}
                    </ul>
                </div>
            )}
            
            {/* O modal agora não precisa da prop 'grupos' */}
            {isModalOpen && <AgendaModal evento={editingEvento} onClose={() => setIsModalOpen(false)} onSave={handleSave} />}
        </div>
    );
}
